import apiClient from './client';

export type ChatScope = 'all' | 'documents' | 'public';
export type ChatModelPath = 'auto' | 'private' | 'public';

export interface ChatCitation {
  type: 'document' | 'public' | 'case_fact';
  ref: string;
  snippet?: string;
  filename?: string;
  source_url?: string;
  washed?: boolean;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  // Metadata attached to assistant answers.
  path_used?: string | null;
  model_used?: string | null;
  run_id?: string | null;
  citations?: ChatCitation[];
  scope?: ChatScope;
}

export interface ChatResponse {
  answer: string;
  citations: ChatCitation[];
  path_used: string | null;
  model_used: string | null;
  run_id: string | null;
}

export async function chatWithCase(
  caseId: string,
  question: string,
  scope: ChatScope = 'all',
  history?: ChatMessage[],
  modelPath?: ChatModelPath
): Promise<ChatResponse> {
  const body: Record<string, unknown> = { question, scope };
  if (modelPath && modelPath !== 'auto') {
    body.model_path = modelPath;
  }
  if (history && history.length) {
    body.history = history.map((m) => ({ role: m.role, content: m.content }));
  }
  const { data } = await apiClient.post<ChatResponse>(
    `/cases/${caseId}/chat`,
    body
  );
  return data;
}
export async function getChatHistory(caseId: string): Promise<ChatMessage[]> {
  const { data } = await apiClient.get<{ messages: ChatMessage[] }>(
    `/cases/${caseId}/chat`
  );
  return data.messages || [];
}

// response from POST /cases/{id}/chat/evaluate-vs-public -- a
// discrepancy list produced by an LLM comparing case claims/facts against
// the latest public-data results (PUBLIC path, cloud allowed).
export interface EvalDiscrepancy {
  claim: string;
  public_value: string;
  source_citation: string;
  severity_note: string;
}

export interface EvaluateVsPublicResponse {
  discrepancies: EvalDiscrepancy[];
  summary: string;
  citations: ChatCitation[];
  model_used: string | null;
  path_used: string | null;
  run_id: string | null;
}

export async function evaluateVsPublic(
  caseId: string,
  runId: string
): Promise<EvaluateVsPublicResponse> {
  const { data } = await apiClient.post<EvaluateVsPublicResponse>(
    `/cases/${caseId}/chat/evaluate-vs-public`,
    { run_id: runId }
  );
  return data;
}

// saved chat responses.
// A reviewer can persist an LLM chat answer to a case (POST), list saved
// responses for a case (GET), and delete one (DELETE, owner/admin only).
export interface SavedChatResponse {
  id: string;
  case_id: string;
  content: string;
  model_used?: string | null;
  path_used?: string | null;
  created_by?: string | null;
  created_at?: string | null;
}

export async function saveChatResponse(
  caseId: string,
  content: string,
  modelUsed?: string | null,
  pathUsed?: string | null
): Promise<SavedChatResponse> {
  const body: Record<string, unknown> = { content };
  if (modelUsed) body.model_used = modelUsed;
  if (pathUsed) body.path_used = pathUsed;
  const { data } = await apiClient.post<SavedChatResponse>(
    `/cases/${caseId}/saved-responses`,
    body
  );
  return data;
}

export async function listSavedChatResponses(
  caseId: string
): Promise<SavedChatResponse[]> {
  const { data } = await apiClient.get<{ items: SavedChatResponse[] }>(
    `/cases/${caseId}/saved-responses`
  );
  return data.items || [];
}

export async function deleteSavedChatResponse(
  savedId: string
): Promise<void> {
  await apiClient.delete(`/saved-responses/${savedId}`);
}
